<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { Menu } from '~/types/models/menu';
import VLazyImage from 'v-lazy-image';
import { Link } from '@inertiajs/vue3';
import WebsiteLogoImage from '@/img/logo.png';
import { closeSubmenu, resolveMenuBySlug } from '~/utils/menu';
import { isClient } from '~/utils/helpers';
import ExternalLinkWarning from '~/components/ui/ExternalLinkWarning.vue';
import { filterMenuItems } from '~/utils/helpers';
import Container from '~/components/Container.vue';
import { useSettings } from '~/composables/useSettings';

const menuActive = ref<boolean>(false);
const openMenuButton = ref<HTMLElement>();
const headerMenu = ref<Menu.Model | null>(null);

const menu = ref<HTMLElement>();
const openMenu = ref<HTMLElement>();

const htmlBody = computed(() => {
  if (isClient()) {
    return document.querySelector('body');
  }

  return undefined;
});

const { headerButtons, websiteLogo } = useSettings();

const handleClose = (event: Event) => {
  if (
    menuActive.value &&
    !(openMenuButton.value === event.target || openMenuButton.value?.contains(event.target as HTMLElement)) &&
    !(openMenu.value === event.target || openMenu.value?.contains(event.target as HTMLElement)) &&
    window.innerWidth > 1280
  ) {
    menuActive.value = false;
  }
  if (event.target && menu.value && !menu.value?.contains(event.target as Node)) {
    closeSubmenu(menu.value);
  }
};

const filteredMenuItems = computed(
  () =>
    headerMenu.value?.menuItems &&
    filterMenuItems(headerMenu.value.menuItems, [
      'TextMenuItem',
      'StaticUrlMenuItem',
      'ColumnMenuItem',
      'ImageMenuItem',
    ])
);

onMounted(() => {
  headerMenu.value = resolveMenuBySlug('header');
  htmlBody.value?.addEventListener('click', handleClose);
});

onBeforeUnmount(() => {
  if (isClient()) {
    htmlBody.value?.removeEventListener('click', handleClose);
  }
});
</script>

<template>
  <header class="header">
    <Container class="header__container">
      <Link href="/" class="header__logo-wrapper">
        <span class="sr-only">Cougar Club</span>
        <VLazyImage :src="websiteLogo || WebsiteLogoImage" alt="Cougar Club" class="header__logo" />
        <span class="header__logo-label">Cougar Club</span>
      </Link>
      <div class="header__nav" :class="{ active: menuActive }">
        <nav :class="{ opened: menuActive }" class="menu">
          <ul class="menu__list">
            <template v-for="menuItem in filteredMenuItems">
              <Component
                :is="menuItem.component"
                v-if="menuItem.component !== null"
                :key="menuItem.id"
                :item="menuItem"
              />
            </template>
          </ul>
        </nav>
        <div v-if="headerButtons && headerButtons.length > 0" class="header__buttons">
          <a
            v-for="(button, index) in headerButtons"
            :key="`header-button-${index}`"
            :href="button.url"
            :target="button.target || '_blank'"
            class="header__button"
          >
            {{ button.label }}
            <ExternalLinkWarning :target="button.target" />
          </a>
        </div>
      </div>
      <button
        ref="openMenuButton"
        class="hamb"
        :class="{ active: menuActive }"
        aria-label="Open Menu"
        @click.prevent.stop="menuActive = !menuActive"
      >
        <span class="sr-only">Open Menu</span>
        <svg class="ham" viewBox="0 0 100 100">
          <path
            class="line top"
            d="m 30,33 h 40 c 3.722839,0 7.5,3.126468 7.5,8.578427 0,5.451959 -2.727029,8.421573 -7.5,8.421573 h -20"
          />
          <path class="line middle" d="m 30,50 h 40" />
          <path
            class="line bottom"
            d="m 70,67 h -40 c 0,0 -7.5,-0.802118 -7.5,-8.365747 0,-7.563629 7.5,-8.634253 7.5,-8.634253 h 20"
          />
        </svg>
      </button>
    </Container>
  </header>
</template>
